import { useStyles, Button, Label, MemberTile } from '@archipro-design/aria';
import { useSearchParams } from 'react-router';
import * as S from './TeamMembers.style';

import React, { useState } from 'react';

import type { TeamMembersResult } from './TeamMembers';
import type { TeamMemberView } from '@modules/professional/type';
import { getMemberPlaceholderImage } from './TeamMembers';
import { ClientOnly } from 'remix-utils';
import { useTracker } from '@archipro-website/tracker';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import TeamMemberDetailsModalUpdated from './TeamMemberDetailsModalUpdated';
import { useProfessionalData } from '@modules/professional/hook/use-professional-data';

export const TeamMembersMobile: React.FC<
    TeamMembersResult & { sectionTitle: string }
> = ({ teamMembers, pageInfo, sectionTitle }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { hasNextPage } = pageInfo;

    const { css } = useStyles();
    const tracker = useTracker();

    const loadMoreTeamMembers = () => {
        tracker.log('ProfessionalAllTeamClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });

        setSearchParams(
            {
                ...searchParams,
                teamPage: ((pageInfo?.currentPage ?? 1) + 1).toString(),
            },
            {
                replace: true,
                preventScrollReset: true,
            },
        );
    };

    return (
        <div className={css(S.TeamMembersMobile)}>
            {sectionTitle && (
                <Label
                    className={css(S.TeamMembersSectionTitleMobile)}
                    variant="01"
                >
                    {sectionTitle}
                </Label>
            )}
            <div className={css(S.TeamMemberGridMobile)}>
                {teamMembers.map((member) => {
                    return <MemberMobile key={member.id} member={member} />;
                })}
            </div>
            {hasNextPage && (
                <div className={css(S.ViewAll)}>
                    <Button
                        transparent={true}
                        onClick={loadMoreTeamMembers}
                        size={18}
                        variables={{
                            buttonPadding: 0,
                        }}
                    >
                        VIEW MORE
                    </Button>
                </div>
            )}
        </div>
    );
};

const MemberMobile: React.FC<{
    member: TeamMemberView;
}> = ({ member }) => {
    const { css } = useStyles();
    const config = useConfigContext();
    const professionalId = useProfessionalData()?.professional.ID;
    const { detailsPageImageSrc, name, jobTitle } = member;
    const [modalOpen, setModalOpen] = useState(false);
    const imageProps = {
        src: detailsPageImageSrc,
        width: 186,
        height: 223,
        unoptimized:
            detailsPageImageSrc ===
            getMemberPlaceholderImage(config.assetsBaseUrl)
                ? true
                : undefined,
    };
    const jobTitleProps = {
        maximumLines: 2,
        children: jobTitle ?? '',
    };

    return (
        <div>
            <ClientOnly fallback={null}>
                {() => (
                    <TeamMemberDetailsModalUpdated
                        member={member}
                        website={member.website ?? ''}
                        toggleModal={() => {
                            setModalOpen(false);
                        }}
                        isOpen={modalOpen}
                        professionalId={professionalId}
                    />
                )}
            </ClientOnly>
            <MemberTile
                image={{
                    ...imageProps,
                    layout: 'fill',
                    alt: name,
                }}
                onClick={() => setModalOpen(true)}
                name={name}
                jobTitle={jobTitleProps}
                onToggleDetails={() => true}
                className={css(S.MemberTileMobile)}
                variables={{
                    tileWidth: 186,
                    tileHeight: 223,
                }}
            />
        </div>
    );
};
